import React, { useState, useEffect } from 'react';

import {
  Button,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InlineLoading,
  Checkbox
} from 'carbon-components-react';

const ExcludeFilesModal: React.FC<Props> = ({
  isModalOpen,
  setIsModalOpen,
  excludeFilesModalData,
  onExcludeButtonClick,
  isUpdatingAttachments,
  updateAttachments
}) => {
  const [changedAttachments, setChangedAttachments] = useState<Array<any>>([]);
  const [filesExcludedCount, setFilesExcludedCount] = useState<number>(0);
  const [reloadContent, setReloadContent] = useState<boolean>(false);

  const handleModalCancel = () => {
    setChangedAttachments([]);
    setIsModalOpen();
    setTimeout(() => {
      setReloadContent(true);
    }, 500);
  };

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const index = changedAttachments.findIndex(
      (element) => element.attachmentId === id
    );
    const newData: Array<any> = changedAttachments;

    const data = {
      attachmentId: id,
      is_hidden: checked,
      enquiryId: excludeFilesModalData.enquiryId
    };
    if (index >= 0) {
      newData.splice(index, 1, data);
    } else {
      newData.push(data);
    }
    setChangedAttachments(newData);

    const checkedCheckboxes = document.querySelectorAll(
      'input[type="checkbox"]:checked'
    ).length;
    setFilesExcludedCount(checkedCheckboxes);
  };

  const handleClickExcludeItems = () => {
    onExcludeButtonClick(changedAttachments);
  };

  useEffect(() => {
    if (excludeFilesModalData?.files) {
      const checked = excludeFilesModalData.files.filter(
        (file: any) => file.is_hidden === true
      );
      setFilesExcludedCount(checked.length);
    }
    setReloadContent(false);
  }, [excludeFilesModalData]);

  useEffect(() => {
    if (!isUpdatingAttachments && updateAttachments) {
      changedAttachments.forEach((attachment: any) => {
        excludeFilesModalData?.files.forEach((file: any) => {
          if (attachment.attachmentId === file.id) {
            file.is_hidden = attachment.is_hidden;
          }
        });
      });
      setIsModalOpen();
    }
  }, [
    updateAttachments,
    isUpdatingAttachments,
    changedAttachments,
    excludeFilesModalData,
    setIsModalOpen
  ]);

  return (
    <div
      id="excludeFilesModal"
      data-testid="excludeFilesModal"
      className="exclude-files-modal-container"
    >
      <ComposedModal
        className="exclude-files-modal"
        data-testid="excludeFilesModal"
        open={isModalOpen}
        onClose={handleModalCancel}
        preventCloseOnClickOutside={true}
      >
        <ModalHeader>
          <div className="exclude-files-modal-header-title">
            {excludeFilesModalData.orgName}
          </div>
        </ModalHeader>

        <ModalBody>
          {!reloadContent && (
            <>
              <div className="exclude-files-modal-body-title">
                Manage response attachments in combined downloads
              </div>
              <div className="exclude-files-modal-body-content">
                <div>Mark to exclude. Unmark to include.</div>
                <div className="checkboxes-container">
                  {excludeFilesModalData.files.map((file: any) => (
                    <Checkbox
                      defaultChecked={file.is_hidden}
                      labelText={file.presentation_name}
                      id={file.id.toString()}
                      key={`file-${file.id}`}
                      onChange={(checked: boolean) =>
                        handleCheckboxChange(file.id, checked)
                      }
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="excluded-files-count">
            {`${filesExcludedCount} file${
              filesExcludedCount > 1 ? 's' : ''
            } excluded`}
          </div>
          <Button
            kind="tertiary"
            onClick={handleModalCancel}
            data-testid="Cancel"
            disabled={isUpdatingAttachments}
          >
            Cancel
          </Button>
          <Button
            kind="secondary"
            data-testid="applyChanges"
            onClick={handleClickExcludeItems}
            disabled={isUpdatingAttachments}
          >
            {!isUpdatingAttachments ? (
              'Apply changes'
            ) : (
              <InlineLoading
                description={
                  !isUpdatingAttachments ? 'Item(s) excluded' : 'Submitting...'
                }
                status={!isUpdatingAttachments ? 'finished' : 'active'}
                aria-live={'assertive'}
              />
            )}
          </Button>
        </ModalFooter>
      </ComposedModal>
    </div>
  );
};

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: Function;
  excludeFilesModalData: {
    orgName: string;
    files: Array<string>;
    enquiryId: number;
  };
  onExcludeButtonClick: Function;
  isUpdatingAttachments: boolean;
  updateAttachments: any;
};

export default React.memo(ExcludeFilesModal);
